import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  Check,
  X,
  RefreshCw,
  Trash2,
  Lock,
  LogOut,
  CalendarX,
  MessageSquare,
  BarChart3,
  ChevronLeft,
  Instagram,
  Plus,
  Edit2,
  Image,
  Video,
  ExternalLink,
  GripVertical,
  Upload,
  Loader2,
  FileVideo,
  Star,
  MessageCircle,
  ClipboardList,
  UserPlus,
  Receipt,
  Settings,
  Ticket,
  CalendarDays,
  Users,
  SlidersHorizontal,
  Dumbbell,
} from "lucide-react";
import axios from "axios";
import RegistrationsSection from "@/components/admin/RegistrationsSection";
import BewerbungenTab from "@/components/admin/BewerbungenTab";
import TermineTab from "@/components/admin/TermineTab";
import TrainingskartenTab from "@/components/admin/TrainingskartenTab";
import { ChangePasswordCard } from "@/components/admin/ChangePasswordCard";
import TrainingAdminTab from "@/components/admin/training/TrainingAdminTab";
import BuchhaltungTab from "@/components/admin/BuchhaltungTab";
import EinstellungenTab from "@/components/admin/EinstellungenTab";
import KalenderTab from "@/components/admin/KalenderTab";
import CancellationAdmin from "@/components/admin/CancellationAdmin";
import AboAdmin from "@/components/admin/AboAdmin";
import FreigabenTab from "@/components/admin/proposals/Proposals";
import KundenTab from "@/components/admin/KundenTab";
import VerfuegbarkeitenTab from "@/components/admin/VerfuegbarkeitenTab";
import RechnungenTab from "@/components/admin/RechnungenTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  rescheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

const paymentStatusColors = {
  pending: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  paid: "bg-green-500/20 text-green-400 border-green-500/30",
  cash: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  failed: "bg-red-500/20 text-red-400 border-red-500/30",
  refunded: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

const paymentStatusLabels = {
  pending: "Zahlung ausstehend",
  paid: "Bezahlt",
  cash: "Barzahlung",
  failed: "Fehlgeschlagen",
  refunded: "Erstattet",
};

const paymentMethodLabels = {
  cash: "Barzahlung",
  paypal: "PayPal",
};

const statusLabels = {
  pending: "Ausstehend",
  confirmed: "Bestätigt",
  cancelled: "Storniert",
  rescheduled: "Verschoben",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("kalender");
  
  // Data states
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [stats, setStats] = useState(null);
  const [timeslots, setTimeslots] = useState([]);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  
  // Dialog states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showInstagramDialog, setShowInstagramDialog] = useState(false);
  const [showTestimonialDialog, setShowTestimonialDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  
  // Form states
  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [newTimeslotId, setNewTimeslotId] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");

  useEffect(() => {
    const savedToken = localStorage.getItem("adminToken");
    if (savedToken) {
      // Validate the stored session before showing the dashboard
      fetch(`${API}/calendar/admin/stats`, { headers: { "X-Admin-Token": savedToken } }).then((r) => {
        if (r.status === 401) localStorage.removeItem("adminToken");
        else { setToken(savedToken); setIsAuthenticated(true); }
      }).catch(() => { setToken(savedToken); setIsAuthenticated(true); });
    }
  }, []);

  // Any 401 from an admin call (expired/invalid session) -> back to the login screen
  useEffect(() => {
    const expire = () => {
      localStorage.removeItem("adminToken");
      setToken("");
      setIsAuthenticated(false);
      toast.error("Sitzung abgelaufen – bitte erneut anmelden", { id: "session-expired" });
    };
    const id = axios.interceptors.response.use((r) => r, (err) => {
      if (err.response?.status === 401 && err.config?.headers?.["X-Admin-Token"]) expire();
      return Promise.reject(err);
    });
    window.addEventListener("admin-auth-expired", expire);
    return () => { axios.interceptors.response.eject(id); window.removeEventListener("admin-auth-expired", expire); };
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      loadData();
    }
  }, [isAuthenticated, token, activeTab]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setToken(response.data.token);
      localStorage.setItem("adminToken", response.data.token);
      setIsAuthenticated(true);
      toast.success("Erfolgreich angemeldet");
    } catch (error) {
      toast.error("Falsches Passwort");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    const old = localStorage.getItem("adminToken");
    if (old) fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logout`, { method: "POST", headers: { "X-Admin-Token": old } }).catch(() => {});
    setToken("");
    setIsAuthenticated(false);
    localStorage.removeItem("adminToken");
    toast.success("Abgemeldet");
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const headers = { "X-Admin-Token": token };
      
      if (activeTab === "bookings" || activeTab === "overview") {
        const params = paymentFilter !== "all" ? { payment_status: paymentFilter } : {};
        const [bookingsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/bookings`, { headers, params }),
          axios.get(`${API}/admin/stats`, { headers }),
        ]);
        setBookings(bookingsRes.data);
        setStats(statsRes.data);
      }
      
      if (activeTab === "contacts") {
        const contactsRes = await axios.get(`${API}/admin/contacts`, { headers });
        setContacts(contactsRes.data);
      }
      
      if (activeTab === "blocked") {
        const blockedRes = await axios.get(`${API}/admin/blocked-dates`, { headers });
        setBlockedDates(blockedRes.data);
      }
      
      if (activeTab === "timeslots") {
        const timeslotsRes = await axios.get(`${API}/admin/timeslots`, { headers });
        setTimeslots(timeslotsRes.data);
      }
      
      if (activeTab === "instagram") {
        const instagramRes = await axios.get(`${API}/admin/instagram/posts`, { headers });
        setInstagramPosts(instagramRes.data);
      }
      
      if (activeTab === "testimonials") {
        const testimonialsRes = await axios.get(`${API}/admin/testimonials`, { headers });
        setTestimonials(testimonialsRes.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Sitzung abgelaufen");
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}`,
        { 
          status, 
          admin_notes: adminNotes,
          cancellation_reason: status === "cancelled" ? cancellationReason : null
        },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Buchung aktualisiert");
      setShowBookingDialog(false);
      setAdminNotes("");
      setCancellationReason("");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!confirm("Buchung wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Buchung gelöscht");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const rescheduleBooking = async () => {
    if (!newTimeslotId) {
      toast.error("Bitte wähle ein neues Zeitfenster");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/bookings/${selectedBooking.id}/reschedule`,
        { new_time_slot_id: newTimeslotId },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Buchung verschoben");
      setShowRescheduleDialog(false);
      setNewTimeslotId("");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Verschieben");
    }
  };

  const blockDateFunc = async () => {
    try {
      await axios.post(
        `${API}/admin/blocked-dates`,
        { date: blockDate, reason: blockReason, is_full_day: true },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Datum blockiert");
      setShowBlockDialog(false);
      setBlockDate("");
      setBlockReason("");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Blockieren");
    }
  };

  const unblockDate = async (blockId) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${blockId}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Blockierung aufgehoben");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aufheben");
    }
  };

  const generateTimeslots = async () => {
    try {
      await axios.post(
        `${API}/admin/timeslots/generate?weeks_ahead=4`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Zeitfenster generiert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Generieren");
    }
  };

  const markContactRead = async (contactId) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${contactId}/read`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      loadData();
    } catch (error) {
      // Silent error - non-critical operation
    }
  };

  const markBookingPaid = async (bookingId) => {
    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/mark-paid`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Zahlung bestätigt");
      setShowBookingDialog(false);
      loadData();
    } catch (error) {
      toast.error("Fehler beim Markieren");
    }
  };

  // Instagram Post Functions
  const [newPost, setNewPost] = useState({
    image_url: "",
    video_url: "",
    thumbnail_url: "",
    caption: "",
    instagram_link: "",
    post_type: "image",
    is_self_hosted: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ungültiger Dateityp. Erlaubt: MP4, WebM, MOV, AVI");
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video zu groß. Maximum: 100MB");
      return;
    }

    setIsUploading(true);
    setUploadProgress("Video wird hochgeladen...");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/upload/video`, formData, {
        headers: {
          "X-Admin-Token": token,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(`Hochladen: ${percent}%`);
        },
      });

      setNewPost({
        ...newPost,
        video_url: response.data.video_url,
        post_type: "video",
        is_self_hosted: true,
      });
      toast.success("Video hochgeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setIsUploading(false);
      setUploadProgress("");
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ungültiger Dateityp. Erlaubt: JPEG, PNG, WebP, GIF");
      return;
    }

    setIsUploading(true);
    setUploadProgress("Thumbnail wird hochgeladen...");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/upload/thumbnail`, formData, {
        headers: {
          "X-Admin-Token": token,
          "Content-Type": "multipart/form-data",
        },
      });

      setNewPost({
        ...newPost,
        thumbnail_url: response.data.thumbnail_url,
      });
      toast.success("Thumbnail hochgeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setIsUploading(false);
      setUploadProgress("");
    }
  };

  const handleSaveInstagramPost = async () => {
    // Validation based on post type
    if (newPost.post_type === "video" && newPost.is_self_hosted) {
      if (!newPost.video_url) {
        toast.error("Bitte lade ein Video hoch");
        return;
      }
    } else {
      if (!newPost.image_url) {
        toast.error("Bitte gib eine Bild-URL ein");
        return;
      }
    }

    try {
      if (editingPost) {
        await axios.patch(
          `${API}/admin/instagram/posts/${editingPost.id}`,
          newPost,
          { headers: { "X-Admin-Token": token } }
        );
        toast.success("Post aktualisiert");
      } else {
        await axios.post(
          `${API}/admin/instagram/posts`,
          { ...newPost, display_order: instagramPosts.length },
          { headers: { "X-Admin-Token": token } }
        );
        toast.success("Post hinzugefügt");
      }
      setShowInstagramDialog(false);
      setEditingPost(null);
      setNewPost({
        image_url: "",
        video_url: "",
        thumbnail_url: "",
        caption: "",
        instagram_link: "",
        post_type: "image",
        is_self_hosted: false,
      });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteInstagramPost = async (postId) => {
    if (!confirm("Post wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/admin/instagram/posts/${postId}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Post gelöscht");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleTogglePostActive = async (post) => {
    try {
      await axios.patch(
        `${API}/admin/instagram/posts/${post.id}`,
        { is_active: !post.is_active },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(post.is_active ? "Post deaktiviert" : "Post aktiviert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleSeedInstagramPosts = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/instagram/seed`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        loadData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const openEditPostDialog = (post) => {
    setEditingPost(post);
    setNewPost({
      image_url: post.image_url || "",
      video_url: post.video_url || "",
      thumbnail_url: post.thumbnail_url || "",
      caption: post.caption || "",
      instagram_link: post.instagram_link || "",
      post_type: post.post_type,
      is_self_hosted: post.is_self_hosted || false,
    });
    setShowInstagramDialog(true);
  };

  const openNewPostDialog = () => {
    setEditingPost(null);
    setNewPost({
      image_url: "",
      video_url: "",
      thumbnail_url: "",
      caption: "",
      instagram_link: "",
      post_type: "image",
      is_self_hosted: false,
    });
    setShowInstagramDialog(true);
  };

  // Testimonial Functions
  const [newTestimonial, setNewTestimonial] = useState({
    quote: "",
    author: "",
    player_age: "",
    rating: 5,
    type: "parent",
  });

  const handleSaveTestimonial = async () => {
    if (!newTestimonial.quote || !newTestimonial.author) {
      toast.error("Zitat und Autor sind erforderlich");
      return;
    }

    try {
      const data = {
        ...newTestimonial,
        player_age: newTestimonial.player_age ? parseInt(newTestimonial.player_age) : null,
      };

      if (editingTestimonial) {
        await axios.patch(
          `${API}/admin/testimonials/${editingTestimonial.id}`,
          data,
          { headers: { "X-Admin-Token": token } }
        );
        toast.success("Bewertung aktualisiert");
      } else {
        await axios.post(
          `${API}/admin/testimonials`,
          { ...data, display_order: testimonials.length },
          { headers: { "X-Admin-Token": token } }
        );
        toast.success("Bewertung hinzugefügt");
      }
      setShowTestimonialDialog(false);
      setEditingTestimonial(null);
      setNewTestimonial({ quote: "", author: "", player_age: "", rating: 5, type: "parent" });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!confirm("Bewertung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Bewertung gelöscht");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleTestimonialActive = async (testimonial) => {
    try {
      await axios.patch(
        `${API}/admin/testimonials/${testimonial.id}`,
        { is_active: !testimonial.is_active },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(testimonial.is_active ? "Bewertung deaktiviert" : "Bewertung aktiviert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleSeedTestimonials = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/testimonials/seed`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        loadData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const openEditTestimonialDialog = (testimonial) => {
    setEditingTestimonial(testimonial);
    setNewTestimonial({
      quote: testimonial.quote,
      author: testimonial.author,
      player_age: testimonial.player_age?.toString() || "",
      rating: testimonial.rating,
      type: testimonial.type,
    });
    setShowTestimonialDialog(true);
  };

  const openNewTestimonialDialog = () => {
    setEditingTestimonial(null);
    setNewTestimonial({ quote: "", author: "", player_age: "", rating: 5, type: "parent" });
    setShowTestimonialDialog(true);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-[#121212] border border-white/10 p-8">
          <div className="flex items-center justify-center mb-8">
            <Lock className="w-12 h-12 text-[#c1ff72]" />
          </div>
          <h1 className="font-['Bebas_Neue'] text-3xl text-white text-center mb-6">
            ADMIN LOGIN
          </h1>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Passwort</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                className="bg-[#1A1A1A] border-white/10 text-white h-12"
                placeholder="Admin-Passwort eingeben"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold py-6 rounded-none"
              data-testid="admin-login-button"
            >
              {isLoading ? "..." : "ANMELDEN"}
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="w-full border-white/20 text-white py-6 rounded-none"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Zurück zur Website
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <h1 className="font-['Bebas_Neue'] text-2xl text-white">
            ADMIN DASHBOARD
          </h1>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              size="sm"
              className="border-white/20 text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Website
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-white/20 text-white"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-[#121212] border border-white/10 p-4">
              <p className="text-[#71717A] text-sm">Gesamt</p>
              <p className="font-['Bebas_Neue'] text-3xl text-white">{stats.total_bookings}</p>
            </div>
            <div className="bg-[#121212] border border-yellow-500/30 p-4">
              <p className="text-yellow-400 text-sm">Ausstehend</p>
              <p className="font-['Bebas_Neue'] text-3xl text-yellow-400">{stats.pending_bookings}</p>
            </div>
            <div className="bg-[#121212] border border-green-500/30 p-4">
              <p className="text-green-400 text-sm">Bestätigt</p>
              <p className="font-['Bebas_Neue'] text-3xl text-green-400">{stats.confirmed_bookings}</p>
            </div>
            <div className="bg-[#121212] border border-red-500/30 p-4">
              <p className="text-red-400 text-sm">Storniert</p>
              <p className="font-['Bebas_Neue'] text-3xl text-red-400">{stats.cancelled_bookings}</p>
            </div>
            <div className="bg-[#121212] border border-[#c1ff72]/30 p-4">
              <p className="text-[#c1ff72] text-sm">Umsatz (bestätigt)</p>
              <p className="font-['Bebas_Neue'] text-3xl text-[#c1ff72]">{stats.total_revenue}€</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "kalender", label: "Kalender", icon: CalendarDays },
            { id: "absagen", label: "Online-Absagen", icon: CalendarX },
            { id: "abos", label: "Monatsabos", icon: Clock },
            { id: "freigaben", label: "Freigaben", icon: Clock },
            { id: "trainingsapp", label: "Trainings-App", icon: Dumbbell },
            { id: "kunden", label: "Kunden", icon: Users },
            { id: "verfuegbarkeiten", label: "Verfügbarkeiten und Buchungsregeln", icon: SlidersHorizontal },
            { id: "rechnungen", label: "Rechnungen", icon: Receipt },
            { id: "bookings", label: "Buchungen", icon: Calendar },
            { id: "termine", label: "Termine & Pakete", icon: Clock },
            { id: "karten", label: "Trainingskarten", icon: Ticket },
            { id: "buchhaltung", label: "Buchhaltung", icon: Receipt },
            { id: "registrations", label: "Camp-Anmeldungen", icon: ClipboardList },
            { id: "bewerbungen", label: "Bewerbungen", icon: UserPlus },
            { id: "contacts", label: "Nachrichten", icon: MessageSquare },
            { id: "blocked", label: "Blockierte Tage", icon: CalendarX },
            { id: "timeslots", label: "Zeitfenster", icon: Clock },
            { id: "instagram", label: "Instagram", icon: Instagram },
            { id: "testimonials", label: "Bewertungen", icon: Star },
            { id: "einstellungen", label: "Einstellungen", icon: Settings },
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              variant={activeTab === tab.id ? "default" : "outline"}
              className={`${
                activeTab === tab.id
                  ? "bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#c1ff72]/90 hover:text-[#0A0A0A]"
                  : "border-white/20 text-white"
              } rounded-none`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
              {tab.id === "contacts" && stats?.unread_contacts > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.unread_contacts}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-[#121212] border border-white/10 p-6">
          {isLoading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Laden...</div>
          ) : (
            <>
              {/* Bookings Tab */}
              {activeTab === "bookings" && (
                <div className="space-y-4">
                  <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                    <h2 className="font-['Bebas_Neue'] text-xl text-white">ALLE BUCHUNGEN</h2>
                    <div className="flex items-center gap-2">
                      <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v); }}>
                        <SelectTrigger className="w-40 bg-[#1A1A1A] border-white/10 text-white h-9">
                          <SelectValue placeholder="Zahlungsstatus" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          <SelectItem value="all" className="text-white">Alle</SelectItem>
                          <SelectItem value="pending" className="text-white">Ausstehend</SelectItem>
                          <SelectItem value="paid" className="text-white">Bezahlt</SelectItem>
                          <SelectItem value="failed" className="text-white">Fehlgeschlagen</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={loadData} variant="outline" size="sm" className="border-white/20 text-white">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Aktualisieren
                      </Button>
                    </div>
                  </div>
                  
                  {bookings.length === 0 ? (
                    <p className="text-[#71717A] text-center py-8">Keine Buchungen vorhanden</p>
                  ) : (
                    <div className="space-y-3">
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="bg-[#1A1A1A] border border-white/5 p-4 hover:border-[#c1ff72]/30 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setAdminNotes(booking.admin_notes || "");
                            setShowBookingDialog(true);
                          }}
                          data-testid={`booking-${booking.id}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex-1 min-w-[200px]">
                              <div className="flex items-center flex-wrap gap-2 mb-2">
                                <User className="w-4 h-4 text-[#c1ff72]" />
                                <span className="text-white font-medium">{booking.customer_name}</span>
                                <Badge className={`${statusColors[booking.status]} border`}>
                                  {statusLabels[booking.status]}
                                </Badge>
                                <Badge className={`${paymentStatusColors[booking.payment_status] || paymentStatusColors.pending} border`}>
                                  {paymentStatusLabels[booking.payment_status] || "Ausstehend"} 
                                  {booking.payment_method === "cash" ? " (Bar)" : " (PayPal)"}
                                </Badge>
                              </div>
                              <div className="text-sm text-[#A1A1AA] space-y-1">
                                <p><Calendar className="w-3 h-3 inline mr-1" />{booking.date} • {booking.start_time}-{booking.end_time}</p>
                                <p><Mail className="w-3 h-3 inline mr-1" />{booking.customer_email}</p>
                                <p><Phone className="w-3 h-3 inline mr-1" />{booking.customer_phone}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-['Bebas_Neue'] text-2xl text-[#c1ff72]">{booking.total_price}€</p>
                              <p className="text-xs text-[#71717A]">{booking.location}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "kalender" && <KalenderTab adminToken={token} />}
              {activeTab === "absagen" && <CancellationAdmin adminToken={token} />}
              {activeTab === "abos" && <AboAdmin adminToken={token} />}
              {activeTab === "freigaben" && <FreigabenTab adminToken={token} />}
              {activeTab === "trainingsapp" && <TrainingAdminTab adminToken={token} />}
              {activeTab === "kunden" && <KundenTab adminToken={token} />}
              {activeTab === "verfuegbarkeiten" && <VerfuegbarkeitenTab adminToken={token} />}
              {activeTab === "rechnungen" && <RechnungenTab adminToken={token} />}

              {/* Registrations Tab (Camps, Akademie, etc.) */}
              {activeTab === "registrations" && (
                <RegistrationsSection token={token} />
              )}

              {/* Bewerbungen Tab (Academy Applications) */}
              {activeTab === "bewerbungen" && (
                <BewerbungenTab adminToken={token} />
              )}

              {/* Termine & Pakete Tab */}
              {activeTab === "karten" && (
                <TrainingskartenTab adminToken={token} />
              )}

              {activeTab === "termine" && (
                <TermineTab adminToken={token} />
              )}

              {/* Buchhaltung Tab */}
              {activeTab === "buchhaltung" && (
                <BuchhaltungTab adminToken={token} />
              )}

              {/* Einstellungen Tab */}
              {activeTab === "einstellungen" && (
                <div className="space-y-6">
                  <ChangePasswordCard adminToken={token} onChanged={handleLogout} />
                  <EinstellungenTab adminToken={token} />
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === "contacts" && (
                <div className="space-y-4">
                  <h2 className="font-['Bebas_Neue'] text-xl text-white mb-4">KONTAKTANFRAGEN</h2>
                  {contacts.length === 0 ? (
                    <p className="text-[#71717A] text-center py-8">Keine Nachrichten</p>
                  ) : (
                    <div className="space-y-3">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className={`bg-[#1A1A1A] border p-4 ${
                            contact.is_read ? "border-white/5" : "border-[#c1ff72]/30"
                          }`}
                          onClick={() => markContactRead(contact.id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{contact.name}</span>
                              {!contact.is_read && (
                                <span className="text-xs bg-[#c1ff72] text-[#0A0A0A] px-2 py-0.5">NEU</span>
                              )}
                            </div>
                            <span className="text-xs text-[#71717A]">
                              {new Date(contact.created_at).toLocaleString("de-DE")}
                            </span>
                          </div>
                          <p className="text-sm text-[#A1A1AA] mb-2">{contact.email} {contact.phone && `• ${contact.phone}`}</p>
                          <p className="text-white">{contact.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Blocked Dates Tab */}
              {activeTab === "blocked" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-['Bebas_Neue'] text-xl text-white">BLOCKIERTE TAGE</h2>
                    <Button
                      onClick={() => setShowBlockDialog(true)}
                      className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
                    >
                      <CalendarX className="w-4 h-4 mr-2" />
                      Tag blockieren
                    </Button>
                  </div>
                  
                  {blockedDates.length === 0 ? (
                    <p className="text-[#71717A] text-center py-8">Keine blockierten Tage</p>
                  ) : (
                    <div className="space-y-2">
                      {blockedDates.map((blocked) => (
                        <div
                          key={blocked.id}
                          className="bg-[#1A1A1A] border border-white/5 p-4 flex justify-between items-center"
                        >
                          <div>
                            <p className="text-white font-medium">{blocked.date}</p>
                            {blocked.reason && <p className="text-sm text-[#A1A1AA]">{blocked.reason}</p>}
                          </div>
                          <Button
                            onClick={() => unblockDate(blocked.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timeslots Tab */}
              {activeTab === "timeslots" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-['Bebas_Neue'] text-xl text-white">ZEITFENSTER</h2>
                    <Button
                      onClick={generateTimeslots}
                      className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      4 Wochen generieren
                    </Button>
                  </div>
                  
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    {timeslots.length} Zeitfenster • {timeslots.filter(t => t.is_available).length} verfügbar
                  </p>
                  
                  <div className="grid gap-2 max-h-[500px] overflow-y-auto">
                    {timeslots.slice(0, 50).map((slot) => (
                      <div
                        key={slot.id}
                        className={`bg-[#1A1A1A] border p-3 flex justify-between items-center ${
                          slot.is_available ? "border-green-500/30" : "border-red-500/30"
                        }`}
                      >
                        <div>
                          <p className="text-white text-sm">{slot.date} • {slot.start_time}-{slot.end_time}</p>
                          <p className="text-xs text-[#71717A]">{slot.location}</p>
                        </div>
                        <Badge className={slot.is_available ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {slot.is_available ? "Frei" : "Belegt"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Instagram Tab */}
              {activeTab === "instagram" && (
                <div className="space-y-4">
                  <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                    <h2 className="font-['Bebas_Neue'] text-xl text-white">INSTAGRAM POSTS</h2>
                    <div className="flex gap-2">
                      {instagramPosts.length === 0 && (
                        <Button
                          onClick={handleSeedInstagramPosts}
                          variant="outline"
                          className="border-white/20 text-white rounded-none"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Beispiel-Posts erstellen
                        </Button>
                      )}
                      <Button
                        onClick={openNewPostDialog}
                        className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Neuer Post
                      </Button>
                    </div>
                  </div>

                  <p className="text-[#A1A1AA] text-sm mb-4">
                    {instagramPosts.length} Posts • {instagramPosts.filter(p => p.is_active).length} aktiv
                  </p>

                  {instagramPosts.length === 0 ? (
                    <div className="text-center py-12">
                      <Instagram className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">Noch keine Instagram Posts</p>
                      <p className="text-[#71717A] text-sm mt-2">
                        Füge deine Instagram-Posts hinzu, um sie auf der Website anzuzeigen.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {instagramPosts.map((post, index) => {
                        // Determine the thumbnail source
                        const thumbnailSrc = post.is_self_hosted && post.thumbnail_url
                          ? (post.thumbnail_url.startsWith('/api') ? `${BACKEND_URL}${post.thumbnail_url}` : post.thumbnail_url)
                          : post.image_url;
                        
                        return (
                          <div
                            key={post.id}
                            className={`bg-[#1A1A1A] border ${
                              post.is_active ? "border-white/10" : "border-red-500/30"
                            } overflow-hidden group`}
                          >
                            <div className="relative aspect-square">
                              {post.is_self_hosted && post.video_url ? (
                                <video
                                  src={`${BACKEND_URL}${post.video_url}`}
                                  poster={thumbnailSrc}
                                  className="w-full h-full object-cover"
                                  muted
                                  onMouseEnter={(e) => e.target.play()}
                                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                />
                              ) : (
                                <img
                                  src={post.image_url}
                                  alt={post.caption || "Instagram Post"}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              {post.post_type === "video" && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
                                  <Video className="w-3 h-3 text-[#0A0A0A]" />
                                </div>
                              )}
                              {post.is_self_hosted && (
                                <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#c1ff72] text-[#0A0A0A] text-xs font-medium">
                                  Eigenes Video
                                </div>
                              )}
                              {!post.is_active && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                  <span className="text-red-400 font-medium">Deaktiviert</span>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openEditPostDialog(post)}
                                  className="bg-white/20 hover:bg-white/30 text-white rounded-none"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleTogglePostActive(post)}
                                  className="bg-white/20 hover:bg-white/30 text-white rounded-none"
                                >
                                  {post.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleDeleteInstagramPost(post.id)}
                                  className="bg-red-500/50 hover:bg-red-500/70 text-white rounded-none"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="p-3">
                              <p className="text-white text-sm line-clamp-2">{post.caption || "Kein Untertitel"}</p>
                              {post.instagram_link && (
                                <a
                                  href={post.instagram_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#c1ff72] text-xs hover:underline flex items-center gap-1 mt-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Instagram öffnen
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="bg-[#1A1A1A] border border-white/10 p-4 mt-6">
                    <h3 className="font-['Bebas_Neue'] text-lg text-white mb-2">ANLEITUNG</h3>
                    <ol className="text-[#A1A1AA] text-sm space-y-2 list-decimal list-inside">
                      <li>Gehe zu deinem Instagram-Post und kopiere die Bild-URL (Rechtsklick → Bildadresse kopieren)</li>
                      <li>Kopiere den Link zu deinem Instagram-Post</li>
                      <li>Klicke auf "Neuer Post" und füge die URLs ein</li>
                      <li>Füge optional einen Untertitel hinzu</li>
                    </ol>
                    <p className="text-[#71717A] text-xs mt-3">
                      Tipp: Du kannst auch Bilder von Unsplash oder anderen Quellen verwenden.
                    </p>
                  </div>
                </div>
              )}

              {/* Testimonials Tab */}
              {activeTab === "testimonials" && (
                <div className="space-y-4">
                  <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                    <h2 className="font-['Bebas_Neue'] text-xl text-white">KUNDENBEWERTUNGEN</h2>
                    <div className="flex gap-2">
                      {testimonials.length === 0 && (
                        <Button
                          onClick={handleSeedTestimonials}
                          variant="outline"
                          className="border-white/20 text-white rounded-none"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Beispiel-Bewertungen
                        </Button>
                      )}
                      <Button
                        onClick={openNewTestimonialDialog}
                        className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Neue Bewertung
                      </Button>
                    </div>
                  </div>

                  <p className="text-[#A1A1AA] text-sm mb-4">
                    {testimonials.length} Bewertungen • {testimonials.filter(t => t.is_active).length} aktiv
                  </p>

                  {testimonials.length === 0 ? (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">Noch keine Bewertungen</p>
                      <p className="text-[#71717A] text-sm mt-2">
                        Füge Kundenbewertungen hinzu, um Vertrauen aufzubauen.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {testimonials.map((testimonial) => (
                        <div
                          key={testimonial.id}
                          className={`bg-[#1A1A1A] border ${
                            testimonial.is_active ? "border-white/10" : "border-red-500/30"
                          } p-4`}
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-grow">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="flex gap-0.5">
                                  {[1,2,3,4,5].map((star) => (
                                    <Star
                                      key={star}
                                      className={`w-4 h-4 ${star <= testimonial.rating ? "text-[#c1ff72] fill-[#c1ff72]" : "text-[#3A3A3A]"}`}
                                    />
                                  ))}
                                </div>
                                {!testimonial.is_active && (
                                  <Badge className="bg-red-500/20 text-red-400 text-xs">Deaktiviert</Badge>
                                )}
                                <Badge className={`text-xs ${testimonial.type === "player" ? "bg-[#c1ff72]/20 text-[#c1ff72]" : "bg-blue-500/20 text-blue-400"}`}>
                                  {testimonial.type === "player" ? "Spieler" : "Eltern"}
                                </Badge>
                              </div>
                              <p className="text-white mb-2">"{testimonial.quote}"</p>
                              <p className="text-[#A1A1AA] text-sm">
                                – {testimonial.author}
                                {testimonial.player_age && ` (${testimonial.player_age})`}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => openEditTestimonialDialog(testimonial)}
                                variant="outline"
                                className="border-white/20 text-white"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleToggleTestimonialActive(testimonial)}
                                variant="outline"
                                className="border-white/20 text-white"
                              >
                                {testimonial.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDeleteTestimonial(testimonial.id)}
                                variant="outline"
                                className="border-red-500/30 text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Booking Detail Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">BUCHUNGSDETAILS</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#71717A]">Name</p>
                  <p className="text-white">{selectedBooking.customer_name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Status</p>
                  <Badge className={`${statusColors[selectedBooking.status]} border`}>
                    {statusLabels[selectedBooking.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-[#71717A]">Email</p>
                  <p className="text-white">{selectedBooking.customer_email}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Telefon</p>
                  <p className="text-white">{selectedBooking.customer_phone}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Datum</p>
                  <p className="text-white">{selectedBooking.date}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Uhrzeit</p>
                  <p className="text-white">{selectedBooking.start_time} - {selectedBooking.end_time}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Standort</p>
                  <p className="text-white">{selectedBooking.location}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Preis</p>
                  <p className="text-[#c1ff72] font-semibold">{selectedBooking.total_price}€</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Zahlungsmethode</p>
                  <p className="text-white">{paymentMethodLabels[selectedBooking.payment_method] || "Unbekannt"}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Zahlungsstatus</p>
                  <Badge className={`${paymentStatusColors[selectedBooking.payment_status] || paymentStatusColors.pending} border`}>
                    {paymentStatusLabels[selectedBooking.payment_status] || "Ausstehend"}
                  </Badge>
                </div>
              </div>
              
              {selectedBooking.message && (
                <div>
                  <p className="text-[#71717A] text-sm">Nachricht</p>
                  <p className="text-white bg-[#1A1A1A] p-2 mt-1">{selectedBooking.message}</p>
                </div>
              )}

              {selectedBooking.payment_method === "paypal" && selectedBooking.payment_status === "pending" && (
                <div className="bg-orange-500/10 border border-orange-500/30 p-4">
                  <p className="text-orange-400 font-medium">PayPal-Zahlung ausstehend</p>
                  <p className="text-sm text-[#A1A1AA]">Der Kunde hat PayPal gewählt. Bestätige die Buchung sobald du die Zahlung in deinem PayPal-Konto siehst.</p>
                  <Button
                    onClick={() => markBookingPaid(selectedBooking.id)}
                    className="mt-3 bg-green-600 hover:bg-green-700 text-white rounded-none"
                    data-testid="mark-paid-button"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Als bezahlt markieren
                  </Button>
                </div>
              )}

              {selectedBooking.payment_method === "cash" && selectedBooking.payment_status === "pending" && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-4">
                  <p className="text-blue-400 font-medium">Barzahlung vor Ort</p>
                  <p className="text-sm text-[#A1A1AA]">Der Kunde zahlt bar beim Training.</p>
                </div>
              )}
              
              <div>
                <Label className="text-[#A1A1AA]">Admin-Notizen</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  placeholder="Interne Notizen..."
                />
              </div>
              
              {selectedBooking.status === "pending" && (
                <div>
                  <Label className="text-[#A1A1AA]">Stornierungsgrund (falls storniert)</Label>
                  <Input
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                    placeholder="Grund für Stornierung..."
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            {selectedBooking?.status === "pending" && (
              <>
                <Button
                  onClick={() => updateBookingStatus(selectedBooking.id, "confirmed")}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-none"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Bestätigen
                </Button>
                <Button
                  onClick={() => updateBookingStatus(selectedBooking.id, "cancelled")}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-none"
                >
                  <X className="w-4 h-4 mr-1" />
                  Stornieren
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setShowBookingDialog(false);
                setShowRescheduleDialog(true);
              }}
              variant="outline"
              className="border-white/20 text-white rounded-none"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Verschieben
            </Button>
            <Button
              onClick={() => deleteBooking(selectedBooking?.id)}
              variant="outline"
              className="border-red-500/30 text-red-400 rounded-none"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Date Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">TAG BLOCKIEREN</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Datum</Label>
              <Input
                type="date"
                value={blockDate}
                onChange={(e) => setBlockDate(e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Grund (optional)</Label>
              <Input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="z.B. Urlaub, Feiertag..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={blockDateFunc}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
            >
              Blockieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">BUCHUNG VERSCHIEBEN</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-[#A1A1AA]">Wähle ein neues Zeitfenster für diese Buchung:</p>
            <Select value={newTimeslotId} onValueChange={setNewTimeslotId}>
              <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white">
                <SelectValue placeholder="Zeitfenster wählen..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                {timeslots
                  .filter((t) => t.is_available)
                  .slice(0, 20)
                  .map((slot) => (
                    <SelectItem key={slot.id} value={slot.id} className="text-white">
                      {slot.date} • {slot.start_time}-{slot.end_time} • {slot.location}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button
              onClick={rescheduleBooking}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
            >
              Verschieben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instagram Post Dialog */}
      <Dialog open={showInstagramDialog} onOpenChange={setShowInstagramDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">
              {editingPost ? "POST BEARBEITEN" : "NEUER POST"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Post Type Selection */}
            <div>
              <Label className="text-[#A1A1AA]">Typ</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setNewPost({ ...newPost, post_type: "image", is_self_hosted: false, video_url: "" })}
                  className={`p-3 border transition-all flex items-center justify-center gap-2 ${
                    newPost.post_type === "image"
                      ? "bg-[#c1ff72]/10 border-[#c1ff72] text-white"
                      : "bg-[#1A1A1A] border-white/10 text-[#A1A1AA] hover:border-white/30"
                  }`}
                >
                  <Image className="w-4 h-4" />
                  Bild
                </button>
                <button
                  type="button"
                  onClick={() => setNewPost({ ...newPost, post_type: "video", is_self_hosted: true })}
                  className={`p-3 border transition-all flex items-center justify-center gap-2 ${
                    newPost.post_type === "video"
                      ? "bg-[#c1ff72]/10 border-[#c1ff72] text-white"
                      : "bg-[#1A1A1A] border-white/10 text-[#A1A1AA] hover:border-white/30"
                  }`}
                >
                  <Video className="w-4 h-4" />
                  Video
                </button>
              </div>
            </div>

            {/* Image Post Fields */}
            {newPost.post_type === "image" && (
              <div>
                <Label className="text-[#A1A1AA]">Bild-URL *</Label>
                <Input
                  value={newPost.image_url}
                  onChange={(e) => setNewPost({ ...newPost, image_url: e.target.value })}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  placeholder="https://..."
                />
                {newPost.image_url && (
                  <div className="mt-2 aspect-square w-32 bg-[#1A1A1A] border border-white/10">
                    <img
                      src={newPost.image_url}
                      alt="Vorschau"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Video Post Fields */}
            {newPost.post_type === "video" && (
              <>
                {/* Video Upload */}
                <div>
                  <Label className="text-[#A1A1AA]">Video hochladen *</Label>
                  <div className="mt-2">
                    {newPost.video_url ? (
                      <div className="space-y-2">
                        <div className="bg-green-500/10 border border-green-500/30 p-3 flex items-center gap-2">
                          <FileVideo className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 text-sm">Video hochgeladen</span>
                        </div>
                        <video
                          src={`${BACKEND_URL}${newPost.video_url}`}
                          className="w-full aspect-video bg-black"
                          controls
                          muted
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setNewPost({ ...newPost, video_url: "" })}
                          className="border-red-500/30 text-red-400"
                        >
                          Video entfernen
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 hover:border-[#c1ff72]/50 bg-[#1A1A1A] cursor-pointer transition-colors">
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                          onChange={handleVideoUpload}
                          className="hidden"
                          disabled={isUploading}
                        />
                        {isUploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-[#c1ff72] animate-spin" />
                            <span className="text-[#A1A1AA] text-sm">{uploadProgress}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-8 h-8 text-[#A1A1AA]" />
                            <span className="text-[#A1A1AA] text-sm">Video auswählen</span>
                            <span className="text-[#71717A] text-xs">MP4, WebM, MOV (max. 100MB)</span>
                          </div>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {/* Caption */}
            <div>
              <Label className="text-[#A1A1AA]">Untertitel (optional)</Label>
              <Input
                value={newPost.caption}
                onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="Beschreibung..."
              />
            </div>

            {/* Instagram Link */}
            <div>
              <Label className="text-[#A1A1AA]">Instagram-Link (optional)</Label>
              <Input
                value={newPost.instagram_link}
                onChange={(e) => setNewPost({ ...newPost, instagram_link: e.target.value })}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="https://instagram.com/p/..."
              />
              <p className="text-[#71717A] text-xs mt-1">
                Link zum Original-Post auf Instagram (wird beim Klick geöffnet)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowInstagramDialog(false)}
              variant="outline"
              className="border-white/20 text-white rounded-none"
              disabled={isUploading}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveInstagramPost}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
              disabled={isUploading}
            >
              {editingPost ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={showTestimonialDialog} onOpenChange={setShowTestimonialDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">
              {editingTestimonial ? "BEWERTUNG BEARBEITEN" : "NEUE BEWERTUNG"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Zitat / Bewertungstext *</Label>
              <Textarea
                value={newTestimonial.quote}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, quote: e.target.value })}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1 min-h-[100px]"
                placeholder="Was sagt der Kunde..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Autor / Name *</Label>
                <Input
                  value={newTestimonial.author}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, author: e.target.value })}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  placeholder="z.B. Vater von Emir"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Alter (optional)</Label>
                <Input
                  type="number"
                  value={newTestimonial.player_age}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, player_age: e.target.value })}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  placeholder="z.B. 12"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Bewertung</Label>
                <Select
                  value={newTestimonial.rating.toString()}
                  onValueChange={(v) => setNewTestimonial({ ...newTestimonial, rating: parseInt(v) })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {[5,4,3,2,1].map((num) => (
                      <SelectItem key={num} value={num.toString()} className="text-white">
                        {"★".repeat(num)}{"☆".repeat(5-num)} ({num})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Typ</Label>
                <Select
                  value={newTestimonial.type}
                  onValueChange={(v) => setNewTestimonial({ ...newTestimonial, type: v })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="parent" className="text-white">Eltern</SelectItem>
                    <SelectItem value="player" className="text-white">Spieler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowTestimonialDialog(false)}
              variant="outline"
              className="border-white/20 text-white rounded-none"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveTestimonial}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] rounded-none"
            >
              {editingTestimonial ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
